/**
 * Users Service
 * API client för användarhantering
 */

import api from '../api/client';

export interface User {
  id: string;
  organization_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  /** RBAC-rollkod (roles.code). null om ingen aktiv roll. */
  role: string | null;
  /** RBAC-rollens visningsnamn (roles.name). */
  role_name?: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'LOCKED';
  auth_method: 'PASSWORD' | 'TWO_FACTOR' | 'SAML2' | 'OIDC';
  saml_name_id: string | null;
  MobilePhone: string | null;
  is_global_admin: boolean;
  mobile_access_enabled?: boolean;
  created_at: string;
  updated_at: string;
  last_login: string | null;
  email_verified?: boolean;
  has_password?: boolean;
  driver_id?: string | null;
  driver_table_id?: number | null;
  current_vehicle_registration?: string | null;
  rsid?: string | null;
  employee_id?: string | null;
  driver?: Driver | null;
}

export interface Driver {
  id: number;
  driver_id: string;
  organization_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  license_number: string | null;
  license_expiry: string | null;
  license_categories: string | null;
  employee_id: string | null;
  department: string | null;
  cost_center: string | null;
  user_id: string | null;
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  organization_id: string;
  password?: string;
  auth_method?: 'PASSWORD' | 'TWO_FACTOR' | 'SAML2' | 'OIDC';
  MobilePhone?: string;
  mobile_access_enabled?: boolean;
  is_driver?: boolean;
  driver_data?: {
    license_number?: string;
    license_expiry?: string;
    license_categories?: string;
    employee_id?: string;
    department?: string;
    cost_center?: string;
  };
  driver_schedule?: {
    mode: 'private_manual' | 'work_hours' | 'company_car';
    schedule?: Array<{ day_of_week: number; start_time: string; end_time: string }>;
  };
}

export interface UpdateUserRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  organization_id?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'LOCKED';
  password?: string;
  app_password?: string;
  auth_method?: 'PASSWORD' | 'TWO_FACTOR' | 'SAML2' | 'OIDC';
  MobilePhone?: string;
  is_global_admin?: boolean;
  mobile_access_enabled?: boolean;
  is_driver?: boolean;
  driver_data?: {
    license_number?: string;
    license_expiry?: string;
    license_categories?: string;
    employee_id?: string;
    department?: string;
    cost_center?: string;
  };
}

class UsersService {
  /**
   * Hämta alla användare
   */
  async getUsers(params?: {
    organizationId?: string;
    includeSubOrgs?: boolean;
  }): Promise<User[]> {
    const queryParams = new URLSearchParams();
    if (params?.organizationId) {
      queryParams.append('organizationId', params.organizationId);
    }
    if (params?.includeSubOrgs) {
      queryParams.append('includeSubOrgs', 'true');
    }

    const response = await api.get(`/api/users?${queryParams.toString()}`);
    return response.data.data;
  }

  /**
   * Hämta användare för val-listor (t.ex. "Bokad av/för").
   * Scopas alltid till angiven organisation – även för global admin – och
   * returnerar endast id/namn/e-post.
   */
  async getUsersForSelect(params?: {
    organizationId?: string;
    includeSubOrgs?: boolean;
  }): Promise<{
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    rsid?: string | null;
    employee_id: string | null;
  }[]> {
    const queryParams = new URLSearchParams();
    if (params?.organizationId) {
      queryParams.append('organizationId', params.organizationId);
    }
    if (params?.includeSubOrgs) {
      queryParams.append('includeSubOrgs', 'true');
    }
    const response = await api.get(`/api/users/for-select?${queryParams.toString()}`);
    return response.data.data;
  }

  /**
   * Hämta en specifik användare
   */
  async getUserById(userId: string): Promise<User> {
    const response = await api.get(`/api/users/${userId}`);
    return response.data.data;
  }

  /**
   * Skapa ny användare
   */
  async createUser(data: CreateUserRequest, sendWelcomeEmail = false): Promise<{ data: User; welcomeEmailSent: boolean }> {
    const response = await api.post('/api/users', { ...data, sendWelcomeEmail });
    return { data: response.data.data, welcomeEmailSent: response.data.welcomeEmailSent ?? false };
  }

  /**
   * Uppdatera användare
   */
  async updateUser(userId: string, data: UpdateUserRequest): Promise<User> {
    const response = await api.put(`/api/users/${userId}`, data);
    return response.data.data;
  }

  /**
   * Ta bort användare (soft delete)
   */
  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/api/users/${userId}`);
  }

  /**
   * Sysadmin-only: är knappen för hard-delete (SSO-omtest) påslagen?
   */
  async getSsoRetestTools(): Promise<{ hardDeleteEnabled: boolean }> {
    const response = await api.get('/api/users/sso-retest-tools');
    return {
      hardDeleteEnabled: Boolean(response.data?.data?.hardDeleteEnabled),
    };
  }

  /**
   * Sysadmin-only: radera användaren helt så SSO kan JIT-skapa kontot igen.
   */
  async hardDeleteUser(userId: string): Promise<void> {
    await api.delete(`/api/users/${userId}/hard-delete`);
  }

  /**
   * Skicka välkomstmail med lösenordsåterställning till en användare
   */
  async sendWelcomeEmail(userId: string): Promise<void> {
    await api.post(`/api/users/${userId}/send-app-invite`);
  }

  /**
   * Skicka välkomstmail till flera användare
   */
  async sendWelcomeEmailBulk(userIds: string[]): Promise<{
    sentCount: number;
    skippedCount: number;
    failedCount: number;
  }> {
    const response = await api.post('/api/users/send-welcome-bulk', { userIds });
    const data = response.data.data;
    return {
      sentCount: data.sentCount,
      skippedCount: data.skippedCount,
      failedCount: data.failedCount,
    };
  }
}

export const usersService = new UsersService();

